import React, {
    useRef,
    useState,
    useEffect,
    useLayoutEffect,
    FunctionComponent,
    RefObject,
    MutableRefObject,
    useCallback
} from 'react';
import { vendorPrefix, support3d } from 'utils/stylePrefix';
import { RouteComponentProps } from 'react-router-dom';
import './index.scss';
import TitleBar, { Props as TitleBarProps } from 'components/common/title-bar';
import { disableSwipe, enableSwipe } from 'utils/native-swipe';

enum Direction {
    Unknown,
    X,
    Y
}

export interface Props extends TitleBarProps, RouteComponentProps<any> {
    leftEdge?: number;
    throttle?: number;
    transitionTime?: number;
    maskOpacity?: number;
    swipeable?: boolean;
    onClose?: () => void;
    shouldSwipeout?: () => boolean;
    closing?: boolean;
    showTitleBar?: boolean;
}

interface Point {
    x: number;
    y: number;
}

function getTouchPoint(e: TouchEvent): Point {
    if (!e) return { x: 0, y: 0 };
    const point = (e.changedTouches && e.changedTouches[0]) || e.touches[0];
    return { x: point.clientX, y: point.clientY };
}

function createCallbacks(
    containerRef: RefObject<HTMLDivElement>,
    maskRef: RefObject<HTMLDivElement>,
    pageWidthRef: MutableRefObject<number>,
    {
        leftEdge = 1,
        throttle = 0.3,
        transitionTime = 500,
        maskOpacity = 0.5,
        history,
        onClose = function () {
            if (history && history.length > 0) {
                history.goBack();
            }
        },
        shouldSwipeout = () => true
    }: Props
) {
    let deltaX = 0;
    let startPoint: Point | null = null;
    let inTransition = false;
    let isSwipingout = false;
    let direction: Direction = Direction.Unknown;
    const transformPrefix = vendorPrefix('transform');
    const transitionPrefix = vendorPrefix('transition');

    const transformPage = (swipeout: boolean, deltaX: number) => {
        const pageWidth = pageWidthRef.current;
        const x = swipeout ? pageWidth : 0;
        const time = swipeout
            ? (transitionTime * (pageWidth - deltaX)) / pageWidth
            : (transitionTime * deltaX) / pageWidth;
        if (containerRef.current) {
            if (time > 0) {
                inTransition = true;
                isSwipingout = swipeout;
            }
            containerRef.current.setAttribute(
                'style',
                `${transformPrefix}: ${
                    support3d ? `translate3d(${x}px, 0, 0)` : `translate(${x}px, 0)`
                }; ${transitionPrefix}: ${transformPrefix} ease ${time}ms`
            );
        }
        if (maskRef.current) {
            const backgroundColor = `hsla(0,0%,0%,${maskOpacity * (1 - x / pageWidth)})`;
            maskRef.current.setAttribute(
                'style',
                `z-index: ${maskRef.current.style.zIndex}; background-color: ${backgroundColor}; ${transitionPrefix}: background-color ease ${time}ms`
            );
        }
    };

    const moveToPosition = (x: number) => {
        if (containerRef.current) {
            containerRef.current.setAttribute(
                'style',
                `${transformPrefix}: ${
                    support3d ? `translate3d(${x}px, 0, 0)` : `translate(${x}px, 0)`
                }`
            );
        }
        if (maskRef.current) {
            const backgroundColor = `hsla(0,0%,0%,${maskOpacity * (1 - x / pageWidthRef.current)})`;
            maskRef.current.setAttribute(
                'style',
                `z-index: ${maskRef.current.style.zIndex}; background-color: ${backgroundColor}`
            );
        }
    };

    const touchstart = (e: TouchEvent) => {
        if (inTransition) {
            return;
        }
        const point = getTouchPoint(e);
        if (point.x > leftEdge * pageWidthRef.current) {
            return;
        }
        direction = Direction.Unknown;
        deltaX = 0;
        startPoint = point;
    };
    const touchmove = (e: TouchEvent) => {
        if (inTransition || !startPoint) {
            return;
        }

        const curPoint = getTouchPoint(e);
        deltaX = curPoint.x - startPoint.x;
        const deltaY = curPoint.y - startPoint.y;
        // 判断用户意图方向
        if (direction === Direction.Unknown) {
            direction = Math.abs(deltaX) > Math.abs(deltaY) ? Direction.X : Direction.Y;
        }
        if (direction === Direction.Y) {
            return;
        }
        e.preventDefault();
        const x = deltaX > 0 ? deltaX : 0;
        moveToPosition(x);
    };
    const touchend = () => {
        if (inTransition || !startPoint || direction !== Direction.X) {
            return;
        }
        const pageWidth = pageWidthRef.current;
        const swipeout = deltaX > throttle * pageWidth && shouldSwipeout();
        transformPage(swipeout, deltaX);
        startPoint = null;
        deltaX = 0;
    };

    const transitionend = (e: TransitionEvent) => {
        if (e.target === containerRef.current) {
            if (isSwipingout) {
                onClose();
            }
            inTransition = false;
        }
    };

    return { touchstart, touchmove, touchend, transitionend, moveToPosition, transformPage };
}
let length = 1;
const SwipeoutPage: FunctionComponent<Props> = (props) => {
    const maskRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const pageWidthRef = useRef(0);
    const { swipeable = true, showTitleBar = true } = props;
    useLayoutEffect(() => {
        pageWidthRef.current = maskRef.current ? maskRef.current.offsetWidth : 0;
    });

    const touchCallbacks = useRef(createCallbacks(containerRef, maskRef, pageWidthRef, props));
    const {
        touchstart,
        touchmove,
        touchend,
        transitionend,
        moveToPosition,
        transformPage
    } = touchCallbacks.current;
    const goPageBack = useCallback(() => {
        props.history.goBack();
    }, []);
    const [moveInAnimated, setMoveInAnimated] = useState(!swipeable);

    useEffect(() => {
        if (props.closing) {
            if (swipeable) {
                transformPage(true, 0);
            } else {
                if (props.onClose) {
                    props.onClose();
                }
            }
        }
    }, [props.closing]);
    // 当展示该组件时，禁用端上右滑返回
    useEffect(() => {
        if (swipeable) {
            disableSwipe();
            return enableSwipe;
        }
    }, [swipeable]);
    useEffect(() => {
        length += 1;
        return () => {
            length -= 1;
        };
    }, []);
    useEffect(() => {
        const container = containerRef.current;
        if (container && swipeable) {
            container.addEventListener('touchstart', touchstart);
            container.addEventListener('touchmove', touchmove);
            container.addEventListener('touchend', touchend);
            container.addEventListener('touchcancel', touchend);
            container.addEventListener('transitionend', transitionend);
            if (!moveInAnimated) {
                moveToPosition(pageWidthRef.current);
                setMoveInAnimated(true);
                setTimeout(() => transformPage(false, pageWidthRef.current), 0);
            }
        }
        return () => {
            if (container && swipeable) {
                // @ts-ignore
                container.removeEventListener('touchstart', touchstart);
                // @ts-ignore
                container.removeEventListener('touchmove', touchmove);
                container.removeEventListener('touchend', touchend);
                container.removeEventListener('touchcancel', touchend);
                container.removeEventListener('transitionend', transitionend);
            }
        };
    }, [swipeable]);
    const lenMemo = React.useMemo(() => length, []);
    return (
        <div
            className={`swipeout-page swipeout-page__mask ${
                !moveInAnimated ? 'swipeout-page__mask--hide' : ''
            }`}
            style={{ zIndex: lenMemo * 10 }}
            ref={maskRef}
        >
            <div
                className={`swipeout-page__container ${
                    !moveInAnimated ? 'swipeout-page__container--hide' : ''
                }`}
                ref={containerRef}
            >
                {showTitleBar ? (
                    <TitleBar
                        title={props.title}
                        showBack={props.showBack}
                        options={props.options}
                        onOptionItemSelected={props.onOptionItemSelected}
                        onBackButtonClicked={props.onBackButtonClicked || goPageBack}
                    />
                ) : null}

                <div className="swipeout-page__body" id="swipeout-page-body">
                    {props.children}
                </div>
            </div>
        </div>
    );
};

export default SwipeoutPage;
